import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {useQuery} from '@tanstack/react-query';
import {useThrottle} from '@uidotdev/usehooks';
import {BoundsObject, Franchisee, FranchiseItem} from '../@types';

interface Variables extends BoundsObject {
  alias?: string[];
  franchises?: string[];
  limit?: number;
}

export const useNearbyFranchiseese = (vars?: Variables) => {
  const request = useGqlRequest({
    query: FRANCHISEE_MANY_NEAR_BY_BRAND,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: Franchisee[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: Franchisee[]}) => data?.result;

  return useQuery({
    queryKey: ['map', 'nearby', 'franchisee', vars],
    enabled: !!vars,
    queryFn,
    select,
    placeholderData: (prev) => prev,
  });
};

export const useSearchFranchiseByKeyword = (value?: string) => {
  const request = useGqlRequest({
    query: SEARCH_FRANCHISE_BY_KEYWORD,
  });
  const keyword = useThrottle(value, 500);

  const queryFn = ():
    | undefined
    | Promise<{
        result?: FranchiseItem[];
      }> => {
    if (!keyword?.length) {
      return;
    }

    return request({keyword});
  };

  const select = (data?: {result?: FranchiseItem[]}) => data?.result;

  return useQuery({
    queryKey: ['search-franchise', keyword],
    enabled: !!keyword?.length,
    queryFn,
    select,
    placeholderData: (prev) => prev,
  });
};

const FRANCHISEE_MANY_NEAR_BY_BRAND = gql`
  query franchiseeNearBy(
    $lat1: Float!
    $lat2: Float!
    $lng1: Float!
    $lng2: Float!
    $franchises: [String!]
    $alias: [String!]
    $limit: Float!
  ) {
    result: franchiseeNearBy(
      lat1: $lat1
      lat2: $lat2
      lng1: $lng1
      lng2: $lng2
      franchises: $franchises
      alias: $alias
      limit: $limit
    ) {
      brandName
      brandId
      businessItemAlias
      id
      location {
        type
        coordinates
      }
      logoUrl
      name
      registrationNumber
    }
  }
`;

export const FRANCHISE_DETAIL_FIELDS = `
  name
  id
  logo {
    url
  }
  franchiseeCount
  businessItems {
    alias
  }
  overriden {
    name
  }
  metadata
`;

const SEARCH_FRANCHISE_BY_KEYWORD = gql`
  query franchiseMany($keyword: String!) {
    result: franchiseMany(
      keyword: $keyword
    ) {
      ${FRANCHISE_DETAIL_FIELDS}
    }
  }
`;
